// Copyright 2013 The Flutter Authors. All rights reserved.
// Use of this source code is governed by a BSD-style license that can be
// found in the LICENSE file.

/** @type {import("./types").WasmAllowList} */
export const defaultWasmSupport = {
  "blink": true,
  "gecko": false,
  "webkit": false,
  "unknown": false,
}

/**
 * @returns {import("./types").BrowserEngine}
 */
const getBrowserEngine = () => {
  if ((navigator.vendor === 'Google Inc.') ||
    (navigator.userAgent.includes('Edg/'))) {
    return "blink";
  }
  if (navigator.vendor === "Apple Computer, Inc.") {
    return "webkit";
  }
  if (navigator.vendor === "" && navigator.userAgent.includes('Firefox')) {
    return "gecko";
  }
  return "unknown";
}

/** @type {import("./types").BrowserEngine} */
const browserEngine = getBrowserEngine();

const hasImageCodecs = () => {
  if (typeof ImageDecoder === "undefined") {
    return false;
  }
  // TODO(yjbanov): https://github.com/flutter/flutter/issues/122761
  // Frequently, when a browser launches an API that other browsers already
  // support, there are subtle incompatibilities that may cause apps to crash if,
  // we blindly adopt the new implementation. This check prevents us from picking
  // up potentially incompatible implementations of ImageDecoder API. Instead,
  // when a new browser engine launches the API, we'll evaluate it and enable it
  // explicitly.
  return browserEngine === "blink";
}

const hasChromiumBreakIterators = () => {
  return (typeof Intl.v8BreakIterator !== "undefined") &&
    (typeof Intl.Segmenter !== "undefined");
}

const hasTextCluster = () => {
  return (typeof window.TextCluster !== "undefined");
}

const getFirefoxVersion = () => {
  const match = navigator.userAgent.match(/firefox\/(\d+)/i);
  return match ? parseInt(match[1], 10) : -1;
}

const supportsDart2Wasm = () => {
  // Firefox < 147 has a SpiderMonkey Ion WasmGC compilation bug that breaks dart2wasm builds.
  // See: https://github.com/flutter/flutter/issues/186619
  //      https://bugzilla.mozilla.org/show_bug.cgi?id=2006811
  if (browserEngine === "gecko" && getFirefoxVersion() < 147) return false;

  // The `<app>.support.js` expression emitted by 
  // ```
  //   % dart compile wasm \
  //          --extra-compiler-option=--require-js-string-builtin \
  //          -o hello.wasm \
  //          hello.dart
  //   % cat hello.support.js
  // ```
  // It checks support for Wasm GC, SIMD, `js-string` builtins, and `try_table`.
  return (WebAssembly.validate(new Uint8Array([0,97,115,109,1,0,0,0,1,5,1,95,1,120,0]))&&WebAssembly.validate(new Uint8Array([0,97,115,109,1,0,0,0,1,5,1,96,0,1,123,3,2,1,0,10,10,1,8,0,65,0,253,15,253,98,11]))&&!WebAssembly.validate(new Uint8Array([0,97,115,109,1,0,0,0,1,4,1,96,0,0,2,23,1,14,119,97,115,109,58,106,115,45,115,116,114,105,110,103,4,99,97,115,116,0,0]),{"builtins":["js-string"]})&&WebAssembly.validate(new Uint8Array([0,97,115,109,1,0,0,0,1,4,1,96,0,0,3,2,1,0,10,13,1,11,0,2,64,31,64,1,2,0,11,11,11])));
}

const detectWebGLVersion = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  if (canvas.getContext('webgl2') != null) {
    return 2;
  }
  if (canvas.getContext('webgl') != null) {
    return 1;
  }
  return -1;
}

const isChromeExtension = () => {
  // Checks for the presence of the Chrome extension ID.
  // See: https://developer.chrome.com/docs/extensions/reference/api/runtime
  return window.chrome && chrome.runtime && chrome.runtime.id;
}

/** @type {import("./types").BrowserEnvironment} */
export const browserEnvironment = {
  browserEngine: browserEngine,
  hasImageCodecs: hasImageCodecs(),
  hasChromiumBreakIterators: hasChromiumBreakIterators(),
  hasTextCluster: hasTextCluster(),
  get supportsDart2Wasm() {
    return supportsDart2Wasm();
  },
  crossOriginIsolated: window.crossOriginIsolated,
  webGLVersion: detectWebGLVersion(),
  isChromeExtension: isChromeExtension(),
};
